import type { AIProvider, Flashcard, GenerationOptions } from '../types';

const POLLINATION_ENDPOINT = 'https://text.pollinations.ai/openai';

interface PollinationResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

/**
 * Build the system prompt for flashcard generation
 */
function buildSystemPrompt(options: GenerationOptions): string {
  const { difficulty, includeHints, outputFormat, language } = options;

  let formatInstructions = '';
  switch (outputFormat) {
    case 'cloze':
      formatInstructions = 'Generate cloze deletion cards where the user fills in blanks.';
      break;
    case 'reversible':
      formatInstructions = 'Generate reversible cards that can be studied from both sides.';
      break;
    default:
      formatInstructions = 'Generate basic question/answer cards.';
  }

  return `You are an expert flashcard creator. Your task is to generate high-quality flashcards from the provided content.

Instructions:
- Difficulty level: ${difficulty}
- Language: ${language}
- ${formatInstructions}
- Each card must have a clear front (question/prompt) and back (answer)
${includeHints ? '- Include a helpful hint for each card' : ''}
- Return the cards in strict JSON format with this structure: {"cards": [{"front": "...", "back": "..."${includeHints ? ', "hint": "..."' : ''}}]}
- Generate exactly the requested number of cards
- Make cards concise but complete (front under 120 chars, back under 200 chars)
- Avoid ambiguous questions or answers
- Use the provided content as the sole source for generating cards`;
}

/**
 * Call the Pollination AI /openai endpoint
 */
export async function generateFlashcards(
  text: string,
  options: GenerationOptions,
  provider?: AIProvider
): Promise<Flashcard[]> {
  const endpoint = provider?.endpoint || POLLINATION_ENDPOINT;
  const apiKey = provider?.apiKey || '';

  const prompt = buildSystemPrompt(options);

  const messages = [
    { role: 'system', content: prompt },
    {
      role: 'user',
      content: `Please generate ${options.cardCount} flashcards from the following content. Return ONLY a valid JSON object with a "cards" array.\n\nContent:\n${text.slice(0, 8000)}`,
    },
  ];

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messages,
    }),
  });

  if (!response.ok) {
    throw new Error(`AI generation failed: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as PollinationResponse;

  if (!data.choices?.[0]?.message?.content) {
    throw new Error('Invalid response from AI service');
  }

  const content = data.choices[0].message.content;

  // Parse the JSON response
  try {
    const jsonStr = extractJsonFromText(content);
    const parsed = JSON.parse(jsonStr);
    return parseFlashcards(parsed.cards || parsed, options);
  } catch (error) {
    throw new Error('Failed to parse AI response: ' + (error as Error).message);
  }
}

/**
 * Extract JSON from text that may include markdown or other formatting
 */
function extractJsonFromText(text: string): string {
  const cleanText = text.trim();
  const codeBlockMatch = cleanText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }
  // Try to find JSON object
  const jsonMatch = cleanText.match(/(\{[\s\S]*\})/);
  if (jsonMatch) {
    return jsonMatch[1].trim();
  }
  return cleanText;
}

/**
 * Parse and validate flashcards from AI response
 */
function parseFlashcards(cards: unknown[], options: GenerationOptions): Flashcard[] {
  if (!Array.isArray(cards)) {
    throw new Error('AI response did not contain a valid cards array');
  }

  return cards.map((card, index) => {
    const cardData = card as Record<string, string>;
    if (!cardData.front || !cardData.back) {
      throw new Error(`Card ${index + 1} is missing required fields`);
    }

    return {
      id: `card-${Date.now()}-${index}`,
      front: cardData.front.trim(),
      back: cardData.back.trim(),
      hint: cardData.hint?.trim() || '',
      tags: options.tags || [],
      difficulty: options.difficulty,
      createdAt: new Date().toISOString(),
      interval: 0,
      easeFactor: 2.5,
      repetitions: 0,
    } as Flashcard;
  });
}

/**
 * Get available AI models for a provider
 */
export function getAvailableModels(): string[] {
  return [
    'openai',
    'openai-large',
    'claude',
    'claude-hybridspace',
    'deepseek',
    'llama',
    'llamallama',
    'mistrallarge',
    'gemini',
    'sur',
    'surmise',
    'unity',
    'openai-audio',
    'llama-scale',
    'rtist',
    'searchgpt',
    'evil',
    'midijourney',
  ];
}

/**
 * Create a default AI provider configuration
 */
export function createDefaultAIProvider(): AIProvider {
  return {
    id: 'pollination',
    name: 'Pollination AI',
    endpoint: POLLINATION_ENDPOINT,
    apiKey: '',
    enabled: true,
    models: getAvailableModels(),
    selectedModel: 'openai',
    maxTokens: 2000,
    temperature: 0.7,
  };
}
